/**
 * 样式引擎 - 自动补全HTML样式
 * 三层策略：规则引擎 + 语义增强 + 模板匹配
 */

/**
 * 默认全局CSS样式
 */
const DEFAULT_GLOBAL_CSS = `
  * { box-sizing: border-box; }
  body {
    font-family: system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    margin: 0;
    padding: 20px;
    color: #1f2937;
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    line-height: 1.6;
    min-height: 100vh;
  }
  h1, h2, h3, h4, h5, h6 {
    margin-top: 0;
    margin-bottom: 0.5em;
    color: #111827;
    font-weight: 600;
  }
  p { margin: 0 0 1em 0; }
  a { color: #2563eb; text-decoration: none; }
  a:hover { text-decoration: underline; }
`;

/**
 * 表单样式
 */
const FORM_STYLES = `
  form {
    max-width: 400px;
    margin: 2rem auto;
    background: white;
    padding: 2rem;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  }
  input, select, textarea {
    width: 100%;
    padding: 0.75rem 1rem;
    margin: 0.5rem 0;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 1rem;
    transition: border-color 0.2s, box-shadow 0.2s;
  }
  input:focus, select:focus, textarea:focus {
    border-color: #2563eb;
    outline: none;
    box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
  }
  button, input[type="submit"] {
    width: 100%;
    padding: 0.75rem 1.5rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 1rem;
    font-weight: 500;
    transition: transform 0.1s, box-shadow 0.2s;
  }
  button:hover, input[type="submit"]:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102,126,234,0.4);
  }
  button:active {
    transform: translateY(0);
  }
  label {
    display: block;
    margin-bottom: 0.25rem;
    font-weight: 500;
    color: #374151;
  }
`;

/**
 * 表格样式
 */
const TABLE_STYLES = `
  table {
    border-collapse: collapse;
    width: 100%;
    background: white;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    margin: 1rem 0;
  }
  th, td {
    padding: 0.75rem 1rem;
    text-align: left;
    border-bottom: 1px solid #e5e7eb;
  }
  th {
    background: #f9fafb;
    font-weight: 600;
    color: #374151;
  }
  tr:hover {
    background: #f9fafb;
  }
`;

/**
 * 卡片样式
 */
const CARD_STYLES = `
  .card, [class*="card"], [class*="box"], [class*="panel"] {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    margin: 1rem 0;
  }
`;

/**
 * 导航样式
 */
const NAV_STYLES = `
  nav, header, [class*="nav"], [class*="header"] {
    background: white;
    padding: 1rem 2rem;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    margin: -20px -20px 20px -20px;
  }
  nav ul, nav ol {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    gap: 1.5rem;
  }
  nav a {
    color: #4b5563;
    font-weight: 500;
  }
  nav a:hover {
    color: #2563eb;
  }
`;

/**
 * 游戏画布样式
 */
const GAME_STYLES = `
  canvas {
    display: block;
    margin: 0 auto;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  }
  .game-container {
    text-align: center;
    padding: 2rem;
  }
  .score {
    font-size: 1.5rem;
    font-weight: bold;
    color: #2563eb;
    margin-bottom: 1rem;
  }
`;

/**
 * 根据HTML内容生成语义化样式
 */
function generateSemanticStyles(html: string): string {
  const rules: string[] = [];
  
  if (/<form/i.test(html) || /<input/i.test(html)) {
    rules.push(FORM_STYLES);
  }
  if (/<table/i.test(html)) {
    rules.push(TABLE_STYLES);
  }
  if (/<nav/i.test(html) || /<header/i.test(html)) {
    rules.push(NAV_STYLES);
  }
  if (/class="[^"]*(card|box|panel)/i.test(html)) {
    rules.push(CARD_STYLES);
  }
  if (/<canvas/i.test(html)) {
    rules.push(GAME_STYLES);
  }
  
  return rules.join('\n');
}

/**
 * 自动补全HTML样式
 * @param html 原始HTML（可能是片段）
 * @param keywords 页面类型关键词
 * @returns 完整的HTML文档
 */
export function autoCompleteStyles(html: string, _keywords: string[] = []): string {
  // 如果已经是完整页面，直接返回
  if (html.includes('<!DOCTYPE') || (html.includes('<html') && html.includes('</html>'))) {
    return html;
  }
  
  // 生成语义化样式
  const semanticStyles = generateSemanticStyles(html);
  
  // 组合完整HTML
  return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>预览</title>
  <style>
    ${DEFAULT_GLOBAL_CSS}
    ${semanticStyles}
  </style>
</head>
<body>
  ${html}
</body>
</html>`;
}

/**
 * 应用设计建议到HTML
 */
export function applyDesignAdvice(html: string, advice: string): string {
  let modified = html;
  
  // 主题颜色
  if (advice.includes('蓝色') || advice.includes('blue')) {
    modified = modified.replace(
      /background:\s*[^;]+;/g,
      'background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);'
    );
  } else if (advice.includes('深色') || advice.includes('dark')) {
    modified = modified.replace(
      /<body[^>]*>/i,
      '<body style="background: #1f2937; color: #f9fafb;">'
    );
  } else if (advice.includes('绿色') || advice.includes('green')) {
    modified = modified.replace(
      /background:\s*[^;]+;/g,
      'background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);'
    );
  }
  
  // 布局
  if (advice.includes('两栏') || advice.includes('两列') || advice.includes('two-column')) {
    modified = modified.replace(
      '</head>',
      '<style>.container { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }</style></head>'
    );
  }
  
  // 圆角
  if (advice.includes('圆角') || advice.includes('rounded')) {
    modified = modified.replace(
      /border-radius:\s*[^;]+;/g,
      'border-radius: 16px;'
    );
  }
  
  return modified;
}
